import React, { useEffect, useState } from 'react';
import api from '@/services/api';
import PageShell from '../../Components/PageShell';
import Spinner from '../../Components/Spinner';
import './PlanUsagePage.css';

const STATUS_LABEL = {
    admin: 'Admin access',
    paid: 'Pro subscription',
    comped: 'Pro (complimentary)',
    trialing: 'Pro trial',
    free: 'Free searches',
};

const fmtDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

const fmtLimit = (value) => (value === null ? 'Unlimited' : value);

const UsageMeter = ({ label, used, limit, unit }) => {
    const unlimited = limit === null;
    const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
    const tone = unlimited ? '' : pct >= 100 ? 'full' : pct >= 80 ? 'warn' : '';

    return (
        <div className="usage-meter">
            <div className="usage-meter-head">
                <span className="usage-meter-label">{label}</span>
                <span className="usage-meter-count">
                    {used} / {unlimited ? '∞' : limit}
                    {unit ? ` ${unit}` : ''}
                </span>
            </div>
            <div className="usage-meter-track">
                <div
                    className={`usage-meter-fill ${tone}`}
                    style={{ width: unlimited ? '4%' : `${pct}%` }}
                />
            </div>
        </div>
    );
};

const PlanCard = ({ plan, isCurrent, badgeLabel }) => (
    <div className={`plan-card ${isCurrent ? 'current' : ''}`}>
        <div className="plan-card-head">
            <span className="plan-card-name">{plan.name}</span>
            {isCurrent && <span className="badge hot">{badgeLabel || 'Your plan'}</span>}
        </div>
        <div className="plan-card-price">
            {plan.priceMonthly === 0 ? (
                <>£0 <small>{plan.searchCredits != null ? 'to try it' : 'forever'}</small></>
            ) : (
                <>£{plan.priceMonthly} <small>/ month{plan.pricePerExtraSeat ? ` + £${plan.pricePerExtraSeat} per extra user` : ''}</small></>
            )}
        </div>
        <ul className="plan-card-features">
            <li>
                {plan.searchCredits != null
                    ? `${plan.searchCredits} free searches, no time limit`
                    : plan.searchesPerMonth === null
                        ? 'Unlimited searches'
                        : `${plan.searchesPerMonth} searches / month`}
            </li>
            <li>
                {plan.resultLimit != null
                    ? `Top ${plan.resultLimit} companies per search`
                    : 'Every company in every search'}
            </li>
            <li>
                {plan.savedTraders === null
                    ? 'Unlimited saved traders'
                    : `Save up to ${plan.savedTraders} traders`}
            </li>
            <li>
                {plan.savedSearches} saved search{plan.savedSearches === 1 ? '' : 'es'}
            </li>
            <li>{plan.historyMonths} months trade history</li>
            <li>{plan.radiusSearch ? 'Postcode radius search' : 'No radius search'}</li>
            <li>{plan.newToTrade ? 'New to trade: fresh leads every month' : 'No New to trade'}</li>
            <li>{plan.mapAreaSearch ? 'Map loads more as you move it' : 'Map of your search results'}</li>
            <li>{plan.seats === null ? 'Unlimited team members' : `${plan.seats} user`}</li>
        </ul>
    </div>
);

const PlanUsagePage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [billingBusy, setBillingBusy] = useState(false);

    useEffect(() => {
        api.get('subscription')
            .then(setData)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const startCheckout = async () => {
        setBillingBusy(true);
        try {
            const seats = data?.usage?.seats || 1;
            const { url } = await api.post('billing/checkout', { seats });
            window.location.href = url;
        } catch (e) {
            setError(e.response?.data?.error || e.message);
            setBillingBusy(false);
        }
    };

    const openBillingPortal = async () => {
        setBillingBusy(true);
        try {
            const { url } = await api.post('billing/portal', {});
            window.location.href = url;
        } catch (e) {
            setError(e.response?.data?.error || e.message);
            setBillingBusy(false);
        }
    };

    if (loading) {
        return (
            <PageShell>
                <Spinner block label="Loading plan" />
            </PageShell>
        );
    }

    if (error || !data) {
        return (
            <PageShell>
                <div className="empty">
                    <span className="kicker">Error</span>
                    <p>Couldn't load your plan details. {error}</p>
                </div>
            </PageShell>
        );
    }

    const { plan, status, trialEndsAt, limits, usage, plans, adminBypass } = data;
    const isFree = plan === 'free';
    // Trials can upgrade early — paid-only features (e.g. downloading saved
    // traders) send trial users here to do exactly that.
    const canUpgrade = isFree || status === 'trialing';
    const priceLine = usage.seats > 1
        ? ` £25/month + £20/month per extra user (${usage.seats} users = £${25 + (usage.seats - 1) * 20}/month).`
        : ' £25/month for your first user.';

    return (
        <PageShell>
            <div className="page-head">
                <span className="kicker">Subscription</span>
                <h1>
                    Plan &amp; <span className="accent">usage.</span>
                </h1>
                <p>
                    {status === 'free' && usage.hadSearchCredits === false
                        ? 'Trial ended'
                        : STATUS_LABEL[status] || 'Your plan'}
                    {status === 'trialing' &&
                        (trialEndsAt
                            ? ` — ends ${fmtDate(trialEndsAt)}.`
                            : ' — full Pro access.')}
                    {status === 'free' && (usage.hadSearchCredits === false
                        ? ' — subscribe to keep searching. Everything you saved is still here.'
                        : ` — ${usage.searchCredits} left. Subscribe any time for unlimited searches and every result.`)}
                </p>
                {adminBypass && (
                    <p className="usage-note">
                        You're an admin — this shows your organisation's plan, but
                        limits are never enforced on admin accounts.
                    </p>
                )}
            </div>

            {canUpgrade && (
                <div className="plan-upgrade-cta">
                    <p>
                        {isFree
                            ? 'Ready for unlimited searches, every company in every search, and more saved traders?'
                            : 'Upgrade now to keep Pro after your trial and unlock paid-only features like downloading your saved traders.'}
                        {priceLine}
                    </p>
                    <button
                        className="btn btn-primary"
                        onClick={startCheckout}
                        disabled={billingBusy}
                    >
                        {billingBusy ? 'Redirecting…' : 'Subscribe'} <span className="arrow">→</span>
                    </button>
                </div>
            )}
            {status === 'paid' && (
                <div className="plan-upgrade-cta">
                    <p>Manage your card, invoices, seats or cancel your subscription.</p>
                    <button
                        className="btn btn-primary"
                        onClick={openBillingPortal}
                        disabled={billingBusy}
                    >
                        {billingBusy ? 'Redirecting…' : 'Manage billing'} <span className="arrow">→</span>
                    </button>
                </div>
            )}

            <section className="section">
                <div className="section-head">
                    <div>
                        <span className="kicker">This month</span>
                        <h2>Your usage.</h2>
                    </div>
                </div>
                <div className="usage-grid">
                    {limits.searchCredits != null ? (
                        <div className="usage-meter">
                            <div className="usage-meter-head">
                                <span className="usage-meter-label">Free searches left</span>
                                <span className="usage-meter-count">{usage.searchCredits}</span>
                            </div>
                            <div className="usage-meter-track">
                                <div
                                    className="usage-meter-fill"
                                    style={{ width: `${Math.round((usage.searchCredits / Math.max(limits.searchCredits, usage.searchCredits, 1)) * 100)}%` }}
                                />
                            </div>
                        </div>
                    ) : (
                        <UsageMeter
                            label="Searches"
                            used={usage.searches}
                            limit={limits.searchesPerMonth}
                        />
                    )}
                    <UsageMeter
                        label="Saved traders"
                        used={usage.savedTraders}
                        limit={limits.savedTraders}
                    />
                    <UsageMeter
                        label="Saved searches"
                        used={usage.savedSearches}
                        limit={limits.savedSearches}
                    />
                    <UsageMeter
                        label="Team members"
                        used={usage.seats}
                        limit={limits.seats}
                    />
                </div>
                <p className="usage-note">
                    {limits.searchCredits != null
                        ? `Free searches don't expire, and a search that finds nothing doesn't count. Each shows the top ${limits.resultLimit} companies.`
                        : 'Searches reset on the 1st of each month.'}{' '}
                    Trade history on your plan covers the last {limits.historyMonths} months.
                </p>
            </section>

            <section className="section">
                <div className="section-head">
                    <div>
                        <span className="kicker">Plans</span>
                        <h2>What's included.</h2>
                    </div>
                </div>
                <div className="plan-grid">
                    <PlanCard plan={plans.free} isCurrent={isFree} />
                    <PlanCard
                        plan={plans.pro}
                        isCurrent={!isFree}
                        badgeLabel={
                            status === 'trialing'
                                ? trialEndsAt
                                    ? `Free trial — ends ${fmtDate(trialEndsAt)}`
                                    : 'Free trial'
                                : 'Your plan'
                        }
                    />
                </div>
            </section>
        </PageShell>
    );
};

export default PlanUsagePage;
